import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { temperatureContext } from '../../../lib/context';
import { CONSTANTS } from '../composables/utils';
import DeviceDetailsModal from './DeviceDetailsModal';

function DeviceCard({ record, isArchived }: any) {
    const { t } = useTranslation(undefined, { keyPrefix: 'RealTime' });
    const tempUnit = useContext(temperatureContext);
    const [openDetail, setOpenDetail] = useState(false);
    const [showToast, setShowToast] = useState(false);

    const color = record.normal ? CONSTANTS.COLORS.CARD_NORMAL : CONSTANTS.COLORS.CARD_ABNORMAL;
    const unitLabel = tempUnit === 'fahrenheit' ? '°F' : '°C';

    const renderArchivedBody = () => (
        <div className='flex aspect-square w-full max-w-[150px] flex-col items-center justify-center overflow-hidden rounded-full bg-[#F1F1F1] shadow-[inset_0_0_5px_rgba(0,0,0,0.25)]'>
            <div className='flex w-full items-center justify-center gap-2 px-5 text-xs font-semibold text-[#737373]'>
                <span className='h-px flex-1 bg-[#b5b5b5]'></span>
                <span>
                    {record.activeUpperLimit}
                    {unitLabel}
                </span>
                <span className='h-px flex-1 bg-[#b5b5b5]'></span>
            </div>
            <div className='my-4 flex items-center justify-center'>
                <img src='/archived.svg' alt='Archived' className='h-[62.5px] w-[62.5px]' />
            </div>
            <div className='flex w-full items-center justify-center gap-2 px-5 text-xs font-semibold text-[#737373]'>
                <span className='h-px flex-1 bg-[#b5b5b5]'></span>
                <span>
                    {record.activeLowerLimit}
                    {unitLabel}
                </span>
                <span className='h-px flex-1 bg-[#b5b5b5]'></span>
            </div>
        </div>
    );

    const renderDisconnectedBody = () => (
        <div className='flex aspect-square w-full max-w-[150px] flex-col items-center justify-center overflow-hidden rounded-full bg-[#F1F1F1] shadow-[inset_0_0_5px_rgba(0,0,0,0.25)]'>
            <div className='flex w-full items-center justify-center gap-2 px-5 text-xs font-semibold text-[#737373]'>
                <span className='h-px flex-1 bg-[#b5b5b5]'></span>
                <span>
                    {record.activeUpperLimit}
                    {unitLabel}
                </span>
                <span className='h-px flex-1 bg-[#b5b5b5]'></span>
            </div>
            <div className='my-4 flex items-center justify-center'>
                <img src='/offline.svg' alt='Offline' className='h-10 w-10' />
            </div>
            <div className='flex w-full items-center justify-center gap-2 px-5 text-xs font-semibold text-[#737373]'>
                <span className='h-px flex-1 bg-[#b5b5b5]'></span>
                <span>
                    {record.activeLowerLimit}
                    {unitLabel}
                </span>
                <span className='h-px flex-1 bg-[#b5b5b5]'></span>
            </div>
        </div>
    );

    return (
        <div
            role='button'
            tabIndex={0}
            aria-label={record.name}
            className='flex h-full w-full cursor-pointer flex-col items-center gap-3 rounded-[28px] bg-white p-4 text-slate-600 transition-all'
            onClick={() => {
                setOpenDetail(true);
            }}
            onKeyDown={(e) => {
                if (e.target !== e.currentTarget) return;
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setOpenDetail(true);
                }
            }}
        >
            <div className='w-full truncate text-center text-sm font-semibold'>{record.name}</div>

            {isArchived ? (
                renderArchivedBody()
            ) : record.connected ? (
                <div
                    className='flex aspect-square w-full max-w-[150px] flex-col items-center justify-center rounded-full border border-slate-200 bg-white'
                    style={{ backgroundImage: `linear-gradient(${color}33 50%, ${color} 50%)` }}
                >
                    <div className='flex w-full items-center justify-center gap-2 px-5 text-xs text-slate-500'>
                        <span className='h-px flex-1 bg-slate-300'></span>
                        <span>
                            {record.activeUpperLimit}
                            {unitLabel}
                        </span>
                        <span className='h-px flex-1 bg-slate-300'></span>
                    </div>
                    <div className='my-3 flex items-center justify-center'>
                        <div className='relative -ml-2 flex items-center justify-center'>
                            <span className='text-4xl font-light text-slate-600'>{record.lastItem.temperature}</span>
                            <span className='absolute -top-1 left-full ml-1 text-sm font-semibold text-slate-600'>
                                {unitLabel}
                            </span>
                        </div>
                    </div>
                    <div className='flex w-full items-center justify-center gap-2 px-5 text-xs text-slate-500'>
                        <span className='h-px flex-1 bg-slate-300'></span>
                        <span>
                            {record.activeLowerLimit}
                            {unitLabel}
                        </span>
                        <span className='h-px flex-1 bg-slate-300'></span>
                    </div>
                </div>
            ) : (
                renderDisconnectedBody()
            )}
            <div className='mt-3 flex w-full justify-center text-xs text-slate-500'>
                <span className='truncate text-[12px]'>
                    {t('deviceID')}: {record.deviceId}
                </span>
            </div>
            {openDetail && (
                <DeviceDetailsModal
                    record={record}
                    open={openDetail}
                    onClose={() => setOpenDetail(false)}
                    isArchived={isArchived}
                />
            )}
        </div>
    );
}

export default DeviceCard;
